import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Download } from 'lucide-react';
import { speakingTopics, audienceTypes, eventFormats, testimonials } from '../mockData';
import SEO from '../components/SEO';

const Speaking = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Speaking Topics & Keynotes"
        description="Book Ravi Prakash for keynote speaking on leadership under pressure, resilience during change, reinvention, and staying human in the age of AI. TEDx speaker with 59K+ views. Perfect for conferences, corporate events, and leadership retreats."
        canonical="/speaking"
      />

      {/* Hero Section */}
      <section className="hero-section relative bg-near-black text-white">
        <div className="hero-overlay"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_leadership-change-1/artifacts/z24zhnnm__DSC3839.jpg')`
          }}
        ></div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="heading-xl text-white mb-6">
            Keynotes that help people lead, adapt, and stay human through change
          </h1>
          <p className="text-xl font-sans text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            Ravi Prakash speaks on leadership, resilience, reinvention, and the human side of change for corporate teams, conferences, universities, graduations, and executive off-sites.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            <a 
              href="https://calendly.com/journeywithravi/30" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary text-lg px-8 py-4"
            >
              Book Ravi
            </a>
            <Link to="/media-kit" className="btn-secondary text-lg px-8 py-4">
              Download Media Kit
            </Link>
          </div>
          
          <p className="text-sm text-gray-400 font-sans">
            Available for keynote talks, fireside chats, panels, leadership conversations, graduations, and book signing appearances.
          </p>
        </div>
      </section>

      {/* What Ravi Brings */}
      <section className="bg-soft-stone section-padding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-8">
            What Ravi brings to the stage
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <p className="text-lg font-sans text-gray-800 leading-relaxed">
              Ravi brings more than a polished talk.
            </p>
            <p className="text-lg font-sans text-gray-800 leading-relaxed">
              He brings lived experience, executive perspective, and a message shaped by pressure, reinvention, and the realities of building a life and career through change.
            </p>
            
            <div className="bg-white rounded-lg p-8 mt-8">
              <p className="text-lg font-sans font-semibold text-charcoal mb-4">
                His talks are known for being:
              </p>
              <ul className="space-y-3">
                {[
                  'Honest without being heavy',
                  'Inspiring without sounding fake',
                  'Practical without being dry',
                  'Human without losing sharpness'
                ].map((item) => (
                  <li key={item} className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-safar-blue mr-3 flex-shrink-0 mt-1" />
                    <span className="text-base font-sans text-gray-800">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-base font-sans text-gray-700 mt-6 italic">
                Audiences leave with perspective, language, and conviction they can actually use.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Signature Topics */}
      <section className="bg-white section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Video Section */}
          <div className="mb-16">
            <h2 className="heading-lg text-charcoal text-center mb-8">
              Watch Ravi Speak
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* TEDx Video */}
              <div>
                <div className="relative rounded-lg overflow-hidden shadow-lg bg-black">
                  <div className="aspect-video">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/ULNDVkxUxOc"
                      title="TEDx Talk: Self-Doubt to Confidence"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                    ></iframe>
                  </div>
                </div>
                <div className="mt-3">
                  <h4 className="text-lg font-serif font-semibold text-charcoal mb-1">
                    TEDx: Self-Doubt to Confidence
                  </h4>
                  <p className="text-sm font-sans text-gray-600 mb-1">TEDx Lake Mary</p>
                  <p className="text-xs font-sans font-semibold text-safar-blue">>59K views</p>
                </div>
              </div>

              {/* Podcast */}
              <div>
                <div className="relative rounded-lg overflow-hidden shadow-lg bg-black">
                  <div className="aspect-video">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/N31Um6aBMks"
                      title="Dare to Declare Podcast"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                    ></iframe>
                  </div>
                </div>
                <div className="mt-3">
                  <h4 className="text-lg font-serif font-semibold text-charcoal mb-1">
                    224 Rejections and One Yes
                  </h4>
                  <p className="text-sm font-sans text-gray-600">Dare to Declare Podcast</p>
                </div>
              </div>

              {/* Ramaiah Institute */}
              <div>
                <div className="relative rounded-lg overflow-hidden shadow-lg bg-black">
                  <div className="aspect-video">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/WQO9Uye1L3E"
                      title="Leadership Talk at Ramaiah Institute"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                    ></iframe>
                  </div>
                </div>
                <div className="mt-3">
                  <h4 className="text-lg font-serif font-semibold text-charcoal mb-1">
                    Leading Yourself
                  </h4>
                  <p className="text-sm font-sans text-gray-600">Ramaiah Institute, Bangalore</p>
                </div>
              </div>
            </div>
          </div>

          {/* Signature Topics */}
          <h2 className="heading-lg text-charcoal text-center mb-12">
            Signature keynote topics
          </h2>
          
          <div className="space-y-12">
            {speakingTopics.map((topic, index) => (
              <div
                key={topic.id}
                className={`grid grid-cols-1 lg:grid-cols-3 gap-8 p-8 rounded-lg ${
                  index % 2 === 0 ? 'bg-soft-stone' : 'bg-soft-white border border-gray-200'
                }`}
              >
                <div className="lg:col-span-1">
                  <h3 className="text-3xl font-serif font-bold text-charcoal mb-4">
                    {topic.title}
                  </h3>
                  <p className="text-base font-sans text-gray-700 leading-relaxed">
                    {topic.fullDescription}
                  </p>
                </div>
                
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <h4 className="font-sans font-semibold text-charcoal mb-3">
                      Audience takeaway:
                    </h4>
                    <ul className="space-y-2">
                      {topic.takeaways.map((takeaway) => (
                        <li key={takeaway} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-safar-orange mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-base font-sans text-gray-700">{takeaway}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="text-sm font-sans font-semibold text-safar-blue">
                      Best for: <span className="font-normal text-gray-700">{topic.bestFor}</span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Book Ravi */}
      <section className="bg-soft-stone section-padding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-8">
            Why event organizers book Ravi
          </h2>
          
          <div className="max-w-3xl mx-auto bg-white rounded-lg p-8 shadow-lg">
            <p className="text-lg font-sans text-gray-800 leading-relaxed mb-6">
              Because audiences do not need another loud speaker with a good smile and five borrowed clichés.
            </p>
            <p className="text-lg font-sans text-gray-800 leading-relaxed mb-6">
              They need someone who can connect across experience levels, speak with substance, and leave people with something that lasts after the applause.
            </p>
            
            <div className="border-t border-gray-200 pt-6">
              <p className="font-sans font-semibold text-charcoal mb-4">
                Ravi combines:
              </p>
              <ul className="space-y-3">
                {[
                  'Lived experience that gives the message weight',
                  'Executive leadership that gives it credibility',
                  'Stage presence that keeps people engaged',
                  'Language that is direct, human, and memorable'
                ].map((item) => (
                  <li key={item} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-safar-blue mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-base font-sans text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="text-center mt-10">
            <a 
              href="https://calendly.com/journeywithravi/30" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center"
            >
              Check Availability
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Audience Types */}
      <section className="bg-white section-padding">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-8">
            Ideal for
          </h2>
          
          <p className="text-lg font-sans text-gray-700 text-center mb-10 max-w-2xl mx-auto">
            Ravi is a strong fit for:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {audienceTypes.map((audience) => (
              <div
                key={audience}
                className="bg-soft-white border border-gray-200 rounded-lg p-6 text-center"
              >
                <p className="text-base font-sans text-gray-800">{audience}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Available Formats */}
      <section className="bg-soft-stone section-padding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-8">
            Available formats
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {eventFormats.map((format) => (
              <div
                key={format}
                className="bg-white rounded-lg p-5 border border-gray-200 flex items-center"
              >
                <CheckCircle className="h-5 w-5 text-safar-orange mr-3 flex-shrink-0" />
                <p className="text-base font-sans text-gray-800">{format}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-sm font-sans text-gray-600 mt-8">
            In-person and virtual options available.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-white section-padding">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-6">
            What audiences remember
          </h2>
          <p className="text-lg font-sans text-gray-700 text-center mb-12 max-w-3xl mx-auto">
            Ravi's talks meet people where they are—without talking down to them, performing at them, or drowning them in clichés.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-soft-white border border-gray-200 rounded-lg p-8"
              >
                <p className="text-lg font-sans text-gray-800 italic mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="border-t border-gray-300 pt-4">
                  <p className="font-sans font-semibold text-charcoal">
                    {testimonial.author}
                  </p>
                  <p className="text-sm font-sans text-gray-600">
                    {testimonial.title}, {testimonial.organization}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-safar-blue text-white section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="heading-lg text-white mb-6">
            Need a speaker who brings both substance and story?
          </h2>
          <p className="text-xl font-sans text-gray-100 mb-10 leading-relaxed">
            If your audience is navigating growth, uncertainty, pressure, reinvention, or the human side of change, Ravi brings a message that is timely, grounded, and memorable.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://calendly.com/journeywithravi/30" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white text-safar-blue hover:bg-gray-100 font-sans font-semibold px-8 py-4 rounded-lg transition-colors"
            >
              Book Ravi
            </a>
            <Link to="/media-kit" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-safar-blue font-sans font-semibold px-8 py-4 rounded-lg transition-colors">
              Download Media Kit
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Speaking;
